import { formatDate } from '@/lib/date-time';
/**
 * @file components/workbench/ManagerWorkbench.tsx
 * @description Manager（業務主管）角色工作台（完整版，使用設計 tokens + shell 組件）。
 *
 * 信息架構（從上到下）：
 *   1. PageShell header — eyebrow + 大標問候 + meta
 *   2. 4 張 StatCard — 待派案件 / SLA 逾期 / 繳費異常 / 本月完成
 *   3. 客戶驗證流程漏斗 — 6 階段在途量（與 Admin 一致）
 *   4. 待派案件 + 異常預警（左右雙欄）
 *   5. 近期動態
 *
 * 數據來源：GET /api/dashboard/manager（useManagerDashboard hook）。
 */

import { Button, Empty, Progress, Table, Tag } from 'antd';
import { useNavigate } from '@tanstack/react-router';
import { formatLongDate } from '@/lib/date-time';
import { useAuthStore } from '@/lib/auth-context';
import { useManagerDashboard } from '@/hooks/useWorkbench';
import { PageShell, PageMetaItem, SectionCard, StatCard } from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';
import type { FunnelStageData, DashboardAlertItem, DashboardActivityItem } from '@zhcb/shared';
import {
  Activity,
  AlertTriangle,
  AlarmClock,
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  DollarSign,
  FileBarChart,
  Inbox,
  TrendingUp,
  TriangleAlert,
} from 'lucide-react';

// ─── 前端本地常量 ─────────────────────────────────────────────────────────────

/**
 * 業務漏斗各阶段的 accent 顏色映射（純前端顯示邏輯，不隨接口變化）。
 * 6 阶段：受理→派案→稽核中→報告審查→驗證決定→已出證，與 Admin 對齊。
 */
const FUNNEL_ACCENT: Record<string, string> = {
  intake: color.sky[500],
  assignment: color.sky[600],
  audit: color.teal[500],
  report: color.teal[600],
  decision: color.emerald[500],
  issued: color.emerald[600],
};

/** 默認 accent 顏色（未知 key 時回退） */
const FUNNEL_DEFAULT_ACCENT = color.sky[500];

/**
 * 業務漏斗阶段 key → 中文顯示名稱映射（純前端顯示邏輯，不隨接口變化）。
 */
const STAGE_LABELS: Record<string, string> = {
  intake: '受理',
  assignment: '派案',
  audit: '稽核中',
  report: '報告審查',
  decision: '驗證決定',
  issued: '已出證',
};

/** 月度目標完成數（前端常量，業務配置 60 件） */
const MONTHLY_TARGET = 60;

// ─── 子組件 ────────────────────────────────────────────────────────────────────

/**
 * 業務漏斗（6 階段橫向）。
 * 接受 stages prop，accent 顏色由本地映射常量決定，與 AdminWorkbench 保持一致。
 */
function BusinessFunnel({ stages }: { stages: FunnelStageData[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${stages.length || 1}, minmax(0, 1fr))`,
        gap: 0,
      }}
    >
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const accent = FUNNEL_ACCENT[stage.key] ?? FUNNEL_DEFAULT_ACCENT;
        return (
          <div
            key={stage.key}
            style={{
              padding: `${space[4]}px ${space[3]}px`,
              borderRight: isLast ? 'none' : `1px solid ${color.border.subtle}`,
              position: 'relative',
            }}
          >
            {/* 階段編號 + 名稱 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: space[2],
                marginBottom: space[3],
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: radius.full,
                  background: accent,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: weight.bold,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: font.mono,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </span>
              <span
                style={{
                  fontSize: text.sm,
                  fontWeight: weight.semibold,
                  color: color.text.secondary,
                  letterSpacing: tracking.wide,
                }}
              >
                {STAGE_LABELS[stage.key] ?? stage.key}
              </span>
            </div>

            {/* 在途量 */}
            <div
              style={{
                fontFamily: font.display,
                fontSize: text['2xl'],
                fontWeight: weight.bold,
                color: color.text.primary,
                lineHeight: leading.tight,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {stage.count}
            </div>

            {/* 平均停留 */}
            <div style={{ marginTop: space[2], fontSize: text.xs, color: color.text.tertiary }}>
              {stage.key === 'issued' ? (
                <>本月新增</>
              ) : (
                <>
                  平均 <strong style={{ color: color.text.secondary }}>{stage.avgDays}</strong> 日
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 異常預警列。
 */
function AlertRow({ alert }: { alert: DashboardAlertItem }) {
  const palette: Record<string, { bg: string; fg: string; icon: typeof TriangleAlert }> = {
    danger: { bg: color.state.dangerSoft, fg: color.state.danger, icon: TriangleAlert },
    warning: { bg: color.state.warningSoft, fg: color.state.warning, icon: AlarmClock },
    info: { bg: color.state.infoSoft, fg: color.state.info, icon: Activity },
  };
  const p = palette[alert.severity] ?? palette.info!;
  const Icon = p.icon;

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.md,
          background: p.bg,
          color: p.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <Icon size={16} strokeWidth={2.2} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.text.primary,
            marginBottom: 2,
            lineHeight: leading.snug,
          }}
        >
          {alert.title}
        </div>
        <div style={{ fontSize: text.sm, color: color.text.tertiary, lineHeight: leading.snug }}>
          {alert.description}
        </div>
      </div>
    </div>
  );
}

/**
 * 近期動態行。
 * DashboardActivityItem 不含 tone/icon，固定使用 Activity 圖標與 info 配色。
 */
function ActivityRow({ event }: { event: DashboardActivityItem }) {
  const p = { bg: color.state.infoSoft, fg: color.state.info };

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: radius.full,
          background: p.bg,
          color: p.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Activity size={14} />
      </div>
      <div style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
        <div style={{ fontSize: text.md, color: color.text.primary, lineHeight: leading.snug }}>
          <span style={{ fontWeight: weight.semibold }}>{event.actor}</span>
          <span style={{ color: color.text.tertiary, margin: '0 4px' }}>{event.action}</span>
          <span style={{ fontWeight: weight.medium }}>{event.target}</span>
        </div>
        <div style={{ fontSize: text.sm, color: color.text.muted, marginTop: 2 }}>{event.time}</div>
      </div>
    </div>
  );
}

// ─── 主組件 ────────────────────────────────────────────────────────────────────

/**
 * Manager 工作台組件。
 * 顯示待派案案件數、SLA 紅燈數、繳費異常數、業務漏斗、異常預警、近期動態。
 * 數據來源：GET /api/dashboard/manager（useManagerDashboard hook）。
 */
export function ManagerWorkbench() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);

  const hour = new Date().getHours();
  const greeting = hour < 6 ? '深夜好' : hour < 12 ? '早安' : hour < 18 ? '午安' : '晚安';

  /** 查詢 Manager Dashboard 聚合數據 */
  const { data: d, isLoading } = useManagerDashboard();

  /** 本月完成度計算（來自真實數據） */
  const monthlyCompletion = d?.certIssuedThisMonth ?? 0;
  const completionPct = Math.round((monthlyCompletion / MONTHLY_TARGET) * 100);

  /** 待派案表格列 */
  const columns = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      width: 160,
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: '客戶',
      dataIndex: 'clientName',
      render: (n: string | null) => n ?? '—',
      ellipsis: true,
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 120,
      render: (v: string) => formatDate(v),
    },
    {
      title: '到期日',
      dataIndex: 'dueDate',
      width: 120,
      render: (v: string | null) => {
        if (!v) return '—';
        const isOverdue = new Date(v) < new Date();
        return (
          <span style={{ color: isOverdue ? '#f5222d' : undefined }}>
            {formatDate(v)}
          </span>
        );
      },
    },
  ];

  return (
    <PageShell
      title={
        <span>
          {greeting}，{user.name}
        </span>
      }
      description={
        <>
          今日有 <strong style={{ color: color.state.danger }}>{d?.pendingDispatch ?? 0}</strong>{' '}
          件待派案，SLA 達成率{' '}
          <strong
            style={{ color: (d?.slaRed ?? 0) > 0 ? color.state.danger : color.state.success }}
          >
            {(d?.slaRed ?? 0) > 0 ? '有逾期' : '正常'}
          </strong>
          。
        </>
      }
      meta={
        <>
          <PageMetaItem
            icon={<CalendarDays size={13} />}
            label="日期"
            value={formatLongDate(new Date())}
          />
          <PageMetaItem
            icon={<Building2 size={13} />}
            label="在途案件"
            value={`${d?.inProgressTotal ?? 0} 件`}
          />
        </>
      }
      actions={
        <>
          <Button
            icon={<FileBarChart size={14} />}
            onClick={() => void navigate({ to: '/analytics' })}
          >
            查看月報
          </Button>
          <Button
            type="primary"
            icon={<Inbox size={14} />}
            onClick={() => void navigate({ to: '/dispatch' })}
          >
            派案中心
          </Button>
        </>
      }
    >
      {/* ═══ 第一排：4 張統計卡 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <StatCard
          title="待派案件"
          value={d?.pendingDispatch ?? 0}
          suffix="件"
          tone="brand"
          icon={<Inbox size={20} />}
          onClick={() => void navigate({ to: '/dispatch' })}
        />
        <StatCard
          title="SLA 逾期案件"
          value={d?.slaRed ?? 0}
          suffix="件"
          tone={(d?.slaRed ?? 0) > 0 ? 'danger' : 'success'}
          icon={<AlertTriangle size={20} />}
        />
        <StatCard
          title="繳費異常客戶"
          value={d?.paymentAbnormal ?? 0}
          suffix="家"
          tone={(d?.paymentAbnormal ?? 0) > 0 ? 'warning' : 'success'}
          icon={<DollarSign size={20} />}
        />
        <StatCard
          title="本月已完成"
          value={monthlyCompletion}
          suffix="件"
          tone="success"
          icon={<CheckCircle2 size={20} />}
          deltaLabel={`目標 ${MONTHLY_TARGET} 件`}
          footer={
            <Progress
              percent={completionPct}
              showInfo={false}
              strokeColor={color.state.success}
              size={['100%', 4]}
            />
          }
        />
      </div>

      {/* ═══ 業務漏斗 ═══ */}
      <div style={{ marginBottom: space[6] }}>
        <SectionCard
          icon={<TrendingUp size={14} />}
          title="業務漏斗"
          description="客戶從受理到出證的全流程在途量"
          actions={
            <Button type="text" size="small" icon={<ArrowRight size={13} />}>
              查看詳情
            </Button>
          }
          padless
        >
          {(d?.funnel ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無資料" />
            </div>
          ) : (
            <BusinessFunnel stages={d?.funnel ?? []} />
          )}
        </SectionCard>
      </div>

      {/* ═══ 待派案件 + 異常預警（左右兩欄） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<Clock size={14} />}
          title="待派案件"
          description="按建立時間排序，逾期案件標紅"
          actions={
            <Button
              type="text"
              size="small"
              icon={<ArrowRight size={13} />}
              onClick={() => void navigate({ to: '/dispatch' })}
            >
              查看全部
            </Button>
          }
          padless
        >
          <Table
            dataSource={d?.pendingCases ?? []}
            rowKey="id"
            size="small"
            loading={isLoading}
            pagination={false}
            onRow={(record) => ({
              onClick: () =>
                void navigate({ to: '/dispatch/$caseId', params: { caseId: String(record.id) } }),
              style: { cursor: 'pointer' },
            })}
            columns={columns}
            locale={{ emptyText: <Empty description="暫無待派案案件" /> }}
          />
        </SectionCard>

        <SectionCard
          icon={<TriangleAlert size={14} />}
          title="異常預警"
          description="按嚴重度排序"
          padless
        >
          {(d?.alerts ?? []).length === 0 ? (
            <div style={{ padding: `${space[6]}px` }}>
              <Empty description="暫無異常預警" />
            </div>
          ) : (
            (d?.alerts ?? []).map((alert) => <AlertRow key={alert.id} alert={alert} />)
          )}
        </SectionCard>
      </div>

      {/* ═══ 近期動態 ═══ */}
      <SectionCard
        icon={<Activity size={14} />}
        title="近期動態"
        description="最新業務動態"
        padless
      >
        {(d?.recentActivity ?? []).length === 0 ? (
          <div style={{ padding: `${space[6]}px` }}>
            <Empty description="暫無近期動態" />
          </div>
        ) : (
          (d?.recentActivity ?? []).map((event) => <ActivityRow key={event.id} event={event} />)
        )}
      </SectionCard>
    </PageShell>
  );
}
